import React from 'react';
import { getSaveDamageDefinition } from '../../../shared/rules/effects/saveDamageDefinitions.mjs';
import { LIGHT_EXTRA_SUFFIX, resolveQuickAttacks } from '../hooks/useQuickAttacks';

const HAND_LABELS = { mainHand: 'Main', offHand: 'Off' };

/** Pinned attacks plus, beside each pinned weapon, its Light extra attack (Nick or Bonus Action) while available. */
export const withLightExtraAttacks = (attacks, items = []) => {
  const seen = new Set();
  return attacks.flatMap(item => {
    const variant = !item.lightExtraAttack && items.find(candidate => candidate.id === `${item.id}${LIGHT_EXTRA_SUFFIX}`);
    return [item, ...(variant ? [variant] : [])].filter(entry => !seen.has(entry.id) && seen.add(entry.id));
  });
};

/** Bottom line of a weapon tile: hand, then attacks left in this Attack action or the Light extra attack mode. */
export const weaponTileDetail = (item, remaining) => {
  const hand = HAND_LABELS[item.slot];
  const use = item.lightExtraAttack ? (item.lightExtraAttack.mode === 'nick' ? 'Nick' : 'Bonus')
    : remaining > 0 && item.attackModes?.ordinaryAvailable !== false ? `${remaining} left` : null;
  return [hand, use].filter(Boolean).join(' · ');
};

export default function QuickAttackControls({ items, pinnedIds, onUse, spellCastBlockReason, combatState, characterId, isActiveTurn = false, activeSpellCast = null, spellCastBusy = false }) {
  const attacks = withLightExtraAttacks(resolveQuickAttacks(items, pinnedIds), items);
  if (!attacks.length) return null;
  const ledger = combatState?.turnActionLedger;
  const currentLedger = isActiveTurn && ledger?.combatantId === String(characterId)
    && ledger.turnSequence === combatState?.turnSequence ? ledger : null;
  const currentAttackAction = currentLedger?.attackAction;
  const offTurn = Number.isInteger(combatState?.activeTurn) && Boolean(combatState?.participants?.length) && !isActiveTurn;
  const allowed = currentAttackAction?.ordinaryAttacksAllowed;
  const spent = currentAttackAction?.ordinaryAttacksSpent;
  const remaining = currentAttackAction?.id && Number.isInteger(allowed) && allowed > 1
    && Number.isInteger(spent) && spent > 0 && spent < allowed ? allowed - spent : 0;
  return <div className="combat-hud-feature-actions" aria-label="Quick attacks">
    {attacks.map(item => {
      const continuingSpell = activeSpellCast?.attackId === item.id
        && activeSpellCast.turnSequence === combatState?.turnSequence && activeSpellCast.completed < activeSpellCast.count;
      const spellResource = item.buffDefinition?.resource || item.healingDefinition?.resource || item.directDefinition?.action || getSaveDamageDefinition(`spell:${item.spell?.spellId}`)?.actionCost?.resource;
      const actionUnavailable = item.spell && spellResource && currentLedger?.[`${spellResource}Spent`] && !continuingSpell;
      const blocked = offTurn || Boolean(actionUnavailable) || Boolean(continuingSpell && spellCastBusy)
        || Boolean(item.spell && spellCastBlockReason) || !(item.canAttackRoll || item.authoritative || item.buffDefinition || item.healingDefinition || item.manualCast);
      const weaponTile = ['weapon', 'unarmed'].includes(item.kind);
      const detail = weaponTile ? weaponTileDetail(item, remaining) : '';
      const light = weaponTile && Boolean(item.lightExtraAttack);
      const label = detail ? `${item.name} (${detail})` : item.name;
      // Sacred Weapon's gold wins over the Light extra attack's, so every tile of the imbued weapon glows alike.
      return <button key={item.id} type="button" data-action-tile={`attack:${item.id}`} className={`combat-hud-resource-tile combat-hud-feature-action-tile${item.sacredWeapon ? ' combat-hud-resource-tile--sacred' : ''}`}
        aria-label={`Use ${label}`} disabled={blocked} style={item.accent || light ? { '--hud-resource-accent': item.accent || '#f5c542' } : undefined}
        title={item.spell && spellCastBlockReason ? spellCastBlockReason : `${label} · ${item.actionType}.${light ? '' : " Remove through the Arsenal's action bar toggle."}`}
        onClick={() => onUse(item)}>
        <span className="combat-hud-resource-tile__icon" aria-hidden="true">{item.icon}</span>
        <span className="combat-hud-resource-tile__name">{item.name}</span>
        {detail && <span className="combat-hud-resource-tile__value">{detail}</span>}
      </button>;
    })}
  </div>;
}
